import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { StatusPill, formatCurrency, formatDateTime } from "@/lib/utils-format";
import { CaretLeft, CheckCircle, XCircle, Question, Paperclip, Truck, ChatCircle, ChatText, UploadSimple, MapPin, ChartLineUp, PencilSimple, Trash, ArrowsClockwise } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import WhatsappButton from "@/components/WhatsappButton";
import { devDebug } from "@/lib/log";

const ORDER_STATUSES = [
  "New Order", "Waiting for Payment", "Payment Under Review", "Payment Confirmed",
  "Ready to Ship", "Shipped", "In Transit", "Delivered", "Cancelled", "Refunded",
];

export default function OrderDetails({ user }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [notes, setNotes] = useState([]);
  const [activity, setActivity] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [profit, setProfit] = useState(null);
  const [tab, setTab] = useState("info");

  const load = useCallback(async () => {
    try {
      const [o, a, n, act, acc, prof] = await Promise.all([
        api.get(`/orders/${id}`),
        api.get(`/orders/${id}/attachments`),
        api.get(`/orders/${id}/notes`),
        api.get(`/orders/${id}/activity`),
        api.get(`/payment-accounts`, { params: { } }),
        api.get(`/dashboard/orders/${id}/profit`).catch(() => ({ data: null })),
      ]);
      setOrder(o.data); setAttachments(a.data); setNotes(n.data); setActivity(act.data); setAccounts(acc.data);
      setProfit(prof.data);
    } catch (error) {
      toast.error("Failed to load order");
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const [editOpen, setEditOpen] = useState(false);

  const deleteOrder = async () => {
    if (!window.confirm(`Delete order #${order.order_number}?\n\nThis will remove:\n· All notes\n· All timeline / activity\n· All attachments (soft)\n· Any linked shipment records\n\nThis cannot be undone.`)) return;
    try {
      await api.delete(`/orders/${id}`);
      toast.success(`Order #${order.order_number} deleted`);
      navigate("/orders");
    } catch (e) {
      const detail = e.response?.data?.detail || "";
      if (e.response?.status === 409 && detail.includes("Payment already verified")) {
        if (!window.confirm(`⚠️ ${detail}\n\nProceed anyway?`)) return;
        try {
          await api.delete(`/orders/${id}?force=true`);
          toast.success(`Order #${order.order_number} force-deleted`);
          navigate("/orders");
        } catch {
          toast.error("Force delete failed");
        }
      } else {
        toast.error(detail || "Delete failed");
      }
    }
  };

  if (!order) return <div className="p-8 text-sm text-zinc-500">Loading…</div>;

  const canVerify = ["admin", "finance"].includes(user.role);
  const canShip = ["admin", "shipping"].includes(user.role);
  const canSeeProfit = ["admin", "finance"].includes(user.role);
  const canEdit = ["admin", "finance", "sales"].includes(user.role);
  const canDelete = user.role === "admin";
  const canResync = ["admin", "finance", "sales"].includes(user.role) && !!order?.import_row_snapshot;

  const resyncFromForm = async () => {
    if (!window.confirm(`Resync order #${order.order_number} from the last Google Form snapshot?\n\nThis will refresh: customer, address, product, quantity, amount, payment method & reference. Status and payment verification stay untouched.`)) return;
    try {
      await api.post(`/orders/${id}/resync-from-form`);
      toast.success("Order resynced from form data");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Resync failed");
    }
  };

  return (
    <div className="p-8" data-testid="order-details-page">
      <button onClick={() => navigate("/orders")} className="flex items-center gap-1 text-sm text-zinc-500 hover:text-zinc-900 mb-4 transition-colors" data-testid="back-to-orders">
        <CaretLeft size={14} /> Back to Orders
      </button>

      <div className="flex items-start justify-between mb-6">
        <div>
          <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold mb-1">Order</p>
          <div className="flex items-center gap-4">
            <h1 className="text-4xl font-black tracking-tighter font-mono" data-testid="order-number">#{order.order_number}</h1>
            <StatusPill status={order.status} />
          </div>
          <p className="text-sm text-zinc-500 mt-2">Created {formatDateTime(order.created_at)}</p>
        </div>
        <div className="text-right">
          <div className="flex items-center gap-2 justify-end mb-2">
            <WhatsappButton
              label="Chat customer"
              message={`Hi ${order.customer_name || "there"}, regarding your order #${order.order_number}…`}
              testId="order-whatsapp-button"
            />
            {canResync && (
              <Button
                variant="outline"
                onClick={resyncFromForm}
                className="border-zinc-300 rounded-md h-8 px-3 text-xs"
                title="Resync from last Google Form snapshot"
                data-testid="resync-order-button"
              >
                <ArrowsClockwise size={13} weight="bold" className="mr-1" />
                Resync from Form
              </Button>
            )}
            {canEdit && (
              <Button
                variant="outline"
                onClick={() => setEditOpen(true)}
                className="border-zinc-300 rounded-md h-8 px-3 text-xs"
                data-testid="edit-order-button"
              >
                <PencilSimple size={13} weight="bold" className="mr-1" />
                Edit
              </Button>
            )}
            {canDelete && (
              <Button
                variant="outline"
                onClick={deleteOrder}
                className="border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700 rounded-md h-8 px-3 text-xs"
                data-testid="delete-order-button"
              >
                <Trash size={13} weight="bold" className="mr-1" />
                Delete
              </Button>
            )}
          </div>
          <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">Total</p>
          <p className="text-3xl font-black font-mono">{formatCurrency(order.total_amount, order.currency)}</p>
        </div>
      </div>

      <EditOrderDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        order={order}
        onSaved={() => { setEditOpen(false); load(); }}
      />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-zinc-100 rounded-md p-1 mb-4">
          <TabsTrigger value="info" className="text-xs uppercase tracking-widest" data-testid="tab-info">Info</TabsTrigger>
          <TabsTrigger value="payment" className="text-xs uppercase tracking-widest" data-testid="tab-payment">Payment</TabsTrigger>
          <TabsTrigger value="attachments" className="text-xs uppercase tracking-widest" data-testid="tab-attachments">Attachments ({attachments.length})</TabsTrigger>
          <TabsTrigger value="notes" className="text-xs uppercase tracking-widest" data-testid="tab-notes">Notes ({notes.length})</TabsTrigger>
          <TabsTrigger value="shipping" className="text-xs uppercase tracking-widest" data-testid="tab-shipping">Shipping</TabsTrigger>
          {canSeeProfit && <TabsTrigger value="profit" className="text-xs uppercase tracking-widest" data-testid="tab-profit">Profit</TabsTrigger>}
          <TabsTrigger value="timeline" className="text-xs uppercase tracking-widest" data-testid="tab-timeline">Timeline</TabsTrigger>
        </TabsList>

        <TabsContent value="info"><InfoTab order={order} profit={profit} canSeeProfit={canSeeProfit} /></TabsContent>
        <TabsContent value="payment"><PaymentTab order={order} accounts={accounts} attachments={attachments} canVerify={canVerify} onDone={load} /></TabsContent>
        <TabsContent value="attachments"><AttachmentsTab orderId={id} attachments={attachments} onDone={load} /></TabsContent>
        <TabsContent value="notes"><NotesTab orderId={id} notes={notes} onDone={load} /></TabsContent>
        <TabsContent value="shipping"><ShippingTab order={order} canShip={canShip} onDone={load} /></TabsContent>
        {canSeeProfit && <TabsContent value="profit"><ProfitTab order={order} profit={profit} /></TabsContent>}
        <TabsContent value="timeline"><TimelineTab activity={activity} /></TabsContent>
      </Tabs>
    </div>
  );
}

function ProfitTab({ order, profit }) {
  if (!profit || profit.error) {
    return <div className="border border-zinc-200 bg-white p-6 text-sm text-zinc-500">No profit data yet — this order may not have cost snapshots.</div>;
  }
  const isVerified = order.payment_verified;
  const marginColor = profit.margin_pct >= 40 ? "text-emerald-700" : profit.margin_pct >= 20 ? "text-amber-700" : profit.margin_pct >= 0 ? "text-zinc-700" : "text-red-700";
  return (
    <div className="space-y-4">
      {!isVerified && (
        <div className="border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900" data-testid="profit-unverified-warning">
          Payment not yet verified — these figures are projected from the order total until payment is confirmed.
        </div>
      )}
      <div className="border border-zinc-200 bg-white p-6" data-testid="profit-card">
        <div className="flex items-center gap-2 mb-4">
          <ChartLineUp size={18} weight="bold" className="text-emerald-700" />
          <h3 className="text-lg font-bold tracking-tight">Profit Breakdown</h3>
        </div>
        <div className="grid grid-cols-4 gap-3 mb-6">
          <ProfitStat label="Revenue" value={formatCurrency(profit.revenue, order.currency)} />
          <ProfitStat label="Total Cost" value={formatCurrency(profit.total_cost, order.currency)} />
          <ProfitStat label="Profit" value={formatCurrency(profit.profit, order.currency)} color={profit.profit >= 0 ? "text-emerald-700" : "text-red-700"} big />
          <ProfitStat label="Margin" value={`${profit.margin_pct}%`} color={marginColor} big />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="border border-zinc-200 p-3 bg-zinc-50">
            <p className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold mb-1">Product + Packing Cost</p>
            <p className="text-lg font-mono font-black">{formatCurrency(profit.cost_products_and_packing, order.currency)}</p>
            <p className="text-[10px] text-zinc-500 mt-1">{profit.shipped_quantity} bags × ({formatCurrency(order.unit_product_cost || 0)} + {formatCurrency(order.unit_packing_cost || 0)})</p>
          </div>
          <div className="border border-zinc-200 p-3 bg-zinc-50">
            <p className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold mb-1">Shipping Cost</p>
            <p className="text-lg font-mono font-black">{formatCurrency(profit.cost_shipping, order.currency)}</p>
            <p className="text-[10px] text-zinc-500 mt-1">{order.tracking_number ? "Actual from courier invoice" : "Estimate from catalog"}</p>
          </div>
        </div>
        {profit.bonus_quantity > 0 && (
          <div className="border border-emerald-200 bg-emerald-50 p-3 mt-3" data-testid="profit-promo-cost">
            <p className="text-[11px] uppercase tracking-widest text-emerald-700 font-semibold">Promo cost (free bags)</p>
            <p className="text-lg font-mono font-black text-emerald-900">
              {profit.bonus_quantity} free bag{profit.bonus_quantity > 1 ? "s" : ""} · {formatCurrency(profit.promo_cost, order.currency)}
            </p>
            <p className="text-[10px] text-emerald-800 mt-1">Included in the product+packing cost above — this is what the promo costs you on this order.</p>
          </div>
        )}
        {order.price_source === "customer" && (
          <p className="text-[11px] text-blue-700 mt-3 font-semibold uppercase tracking-widest">★ Priced using this customer's custom tiers</p>
        )}
      </div>
    </div>
  );
}

function ProfitStat({ label, value, color = "", big }) {
  return (
    <div className="border border-zinc-200 p-3 bg-white">
      <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">{label}</p>
      <p className={`font-black tracking-tighter font-mono mt-1 ${big ? "text-2xl" : "text-lg"} ${color}`}>{value}</p>
    </div>
  );
}

function InfoTab({ order, profit, canSeeProfit }) {
  const hasBonus = (order.bonus_quantity || 0) > 0;
  const marginColor = profit && profit.margin_pct >= 40 ? "text-emerald-700" : profit && profit.margin_pct >= 20 ? "text-amber-700" : profit && profit.margin_pct >= 0 ? "text-zinc-700" : "text-red-700";
  return (
    <div className="space-y-4">
      {canSeeProfit && profit && !profit.error && (
        <div className="border border-zinc-200 bg-white p-6" data-testid="info-pricing-card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold tracking-tight">Pricing & Cost</h3>
            {!order.payment_verified && (
              <span className="text-[10px] uppercase tracking-widest text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 font-semibold">Projected — payment not verified</span>
            )}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
            <ProfitStat label="Selling Total" value={formatCurrency(profit.revenue, order.currency)} big data-testid="info-selling" />
            <ProfitStat label="Cost (all-in)" value={formatCurrency(profit.total_cost, order.currency)} big />
            <ProfitStat label="Profit" value={formatCurrency(profit.profit, order.currency)} color={profit.profit >= 0 ? "text-emerald-700" : "text-red-700"} big />
            <ProfitStat label="Margin" value={`${profit.margin_pct}%`} color={marginColor} big />
          </div>
          <div className="grid grid-cols-3 gap-3 text-xs">
            <div className="border border-zinc-200 p-3 bg-zinc-50">
              <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Product cost</p>
              <p className="font-mono font-black mt-1">{formatCurrency((order.unit_product_cost || 0) * (profit.shipped_quantity || 0), order.currency)}</p>
              <p className="text-[10px] text-zinc-500 mt-0.5">{profit.shipped_quantity} bags × {formatCurrency(order.unit_product_cost || 0, order.currency)}</p>
            </div>
            <div className="border border-zinc-200 p-3 bg-zinc-50">
              <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Packing cost</p>
              <p className="font-mono font-black mt-1">{formatCurrency((order.unit_packing_cost || 0) * (profit.shipped_quantity || 0), order.currency)}</p>
              <p className="text-[10px] text-zinc-500 mt-0.5">{profit.shipped_quantity} bags × {formatCurrency(order.unit_packing_cost || 0, order.currency)}</p>
            </div>
            <div className="border border-zinc-200 p-3 bg-zinc-50">
              <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Shipping cost</p>
              <p className="font-mono font-black mt-1">{formatCurrency(profit.cost_shipping, order.currency)}</p>
              <p className="text-[10px] text-zinc-500 mt-0.5">{order.tracking_number ? "Courier invoice" : "Catalog estimate"}</p>
            </div>
          </div>
          {hasBonus && (
            <p className="text-[11px] text-emerald-700 mt-3 font-semibold uppercase tracking-widest">
              ★ Promo: buy {order.quantity} · ship {profit.shipped_quantity} ({order.bonus_quantity} free bag{order.bonus_quantity > 1 ? "s" : ""} · {formatCurrency(profit.promo_cost, order.currency)} cost)
            </p>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card title="Customer">
          <Row label="Name" value={order.customer_name} />
          <Row label="Company" value={order.company} />
          <Row label="Email" value={order.email} />
          <Row label="Phone" value={order.phone} />
          <Row label="Country" value={order.country} />
          <Row label="Address" value={order.address} />
        </Card>
        <Card title="Order">
          <Row label="Form Order #" value={order.external_order_number || "—"} />
          <Row label="Product" value={order.product} />
          <Row label="Quantity (billed)" value={order.quantity} />
          {hasBonus && <Row label="Bonus bags (free)" value={`+${order.bonus_quantity}`} />}
          {hasBonus && <Row label="Shipped quantity" value={order.shipped_quantity || (order.quantity + order.bonus_quantity)} />}
          <Row label="Weight" value={order.weight ? `${order.weight} kg` : "—"} />
          <Row label="Amount" value={formatCurrency(order.total_amount, order.currency)} />
          <Row label="Currency" value={order.currency} />
          <Row label="Payment Method" value={order.payment_method} />
          <Row label="Payment Reference" value={order.payment_reference} />
          <Row label="Remarks" value={order.remarks} />
        </Card>
      </div>
    </div>
  );
}

function PaymentTab({ order, accounts, attachments, canVerify, onDone }) {
  const [accountId, setAccountId] = useState("");
  const [amount, setAmount] = useState(order.total_amount);
  const [txnId, setTxnId] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);

  const proofs = (attachments || []).filter((a) => a.kind === "payment_proof");

  const act = async (action) => {
    if (action === "approve" && !accountId) {
      toast.error("Select the receiving account first");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/orders/${order.order_id}/verify-payment`, {
        action, payment_account_id: action === "approve" ? accountId : null,
        amount_received: parseFloat(amount) || null, transaction_id: txnId, notes,
      });
      toast.success(`Payment ${action === "approve" ? "approved" : action === "reject" ? "rejected" : "info requested"}`);
      onDone();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <Card title="Payment Status">
        <Row label="Verified" value={order.payment_verified ? "Yes" : "No"} />
        <Row label="Allocated To" value={order.payment_account_id ? accounts.find((a) => a.account_id === order.payment_account_id)?.account_name : "—"} />
        <Row label="Amount Received" value={order.amount_received ? formatCurrency(order.amount_received, order.currency) : "—"} />
        <Row label="Transaction ID" value={order.transaction_id} />      </Card>

      {canVerify && !order.payment_verified && (
        <Card title="Verify Payment">
          <div className="space-y-3">
            <div>
              <Label className="text-[11px] uppercase tracking-widest text-zinc-500">Receiving Account</Label>
              <Select value={accountId} onValueChange={setAccountId}>
                <SelectTrigger className="border-zinc-300 rounded-md mt-1" data-testid="allocate-account-select"><SelectValue placeholder="Select account…" /></SelectTrigger>
                <SelectContent>
                  {accounts.filter((a) => a.active).map((a) => (
                    <SelectItem key={a.account_id} value={a.account_id}>
                      {a.account_name} ({a.currency}, {a.country})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[11px] uppercase tracking-widest text-zinc-500">Amount</Label>
                <Input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className="border-zinc-300 rounded-md mt-1" data-testid="amount-received-input" />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-widest text-zinc-500">Transaction ID</Label>
                <Input value={txnId} onChange={(e) => setTxnId(e.target.value)} className="border-zinc-300 rounded-md mt-1" data-testid="transaction-id-input" />
              </div>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-widest text-zinc-500">Notes (for reject / need info emails)</Label>
              <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="border-zinc-300 rounded-md mt-1" data-testid="verify-notes-input" />
            </div>
            <div className="flex gap-2 pt-2">
              <Button onClick={() => act("approve")} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-md flex-1" data-testid="approve-payment-button">
                <CheckCircle size={16} weight="bold" className="mr-1" /> Approve
              </Button>
              <Button onClick={() => act("reject")} disabled={busy} variant="outline" className="border-red-300 text-red-700 hover:bg-red-50 rounded-md" data-testid="reject-payment-button">
                <XCircle size={16} weight="bold" className="mr-1" /> Reject
              </Button>
              <Button onClick={() => act("need_info")} disabled={busy} variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-50 rounded-md" data-testid="need-info-button">
                <Question size={16} weight="bold" className="mr-1" /> Need Info
              </Button>
            </div>
          </div>
        </Card>
      )}

      {proofs.length > 0 && (
        <div className="lg:col-span-2 border border-zinc-200 bg-white p-6" data-testid="payment-proof-gallery">
          <div className="flex items-center gap-2 mb-4">
            <Paperclip size={16} weight="bold" />
            <h3 className="text-lg font-bold tracking-tight">Payment Proofs ({proofs.length})</h3>
            <span className="text-xs text-zinc-500 ml-2">Click any proof to preview it full-size — no download needed.</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {proofs.map((att) => {
              const src = att.download_url ? `${process.env.REACT_APP_BACKEND_URL}${att.download_url}` : att.data_url;
              const dlHref = att.download_url ? `${process.env.REACT_APP_BACKEND_URL}${att.download_url}?inline=0` : att.data_url;
              const isImage = att.content_type?.startsWith("image/");
              const isPdf = att.content_type === "application/pdf";
              return (
                <button
                  key={att.attachment_id}
                  type="button"
                  onClick={() => setPreview({ src, dlHref, filename: att.filename, content_type: att.content_type })}
                  className="text-left group"
                  data-testid={`payment-proof-preview-${att.attachment_id}`}
                >
                  {isImage ? (
                    <img src={src} alt={att.filename} className="w-full h-32 object-cover border border-zinc-200 group-hover:opacity-90 transition-opacity" />
                  ) : isPdf ? (
                    <div className="w-full h-32 flex flex-col items-center justify-center bg-zinc-100 border border-zinc-200 group-hover:bg-zinc-200 transition-colors">
                      <Paperclip size={20} className="text-zinc-500 mb-1" />
                      <span className="text-xs text-zinc-700 font-semibold">PDF</span>
                    </div>
                  ) : (
                    <div className="w-full h-32 flex items-center justify-center bg-zinc-100 border border-zinc-200 text-xs text-zinc-600 px-2 text-center">{att.filename}</div>
                  )}
                  <p className="text-[10px] text-zinc-500 mt-1 truncate">{formatDateTime(att.created_at)}</p>
                </button>
              );
            })}
          </div>
          <AttachmentPreviewDialog preview={preview} onClose={() => setPreview(null)} />
        </div>
      )}
    </div>
  );
}

function AttachmentsTab({ orderId, attachments, onDone }) {
  const [file, setFile] = useState(null);
  const [kind, setKind] = useState("payment_proof");
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null); // {src, filename, content_type, dlHref}

  const upload = async () => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("kind", kind);
    try {
      await api.post(`/orders/${orderId}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Attachment uploaded");
      setFile(null);
      onDone();
    } catch { toast.error("Upload failed"); } finally { setBusy(false); }
  };

  return (
    <div>
      <Card title="Upload Attachment">
        <div className="flex flex-col md:flex-row gap-3 items-end">
          <div className="flex-1">
            <Label className="text-[11px] uppercase tracking-widest text-zinc-500">File (PDF, JPG, PNG)</Label>
            <Input type="file" accept="image/*,application/pdf" onChange={(e) => setFile(e.target.files[0])} className="border-zinc-300 rounded-md mt-1" data-testid="attachment-file-input" />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-widest text-zinc-500">Kind</Label>
            <Select value={kind} onValueChange={setKind}>
              <SelectTrigger className="w-[200px] border-zinc-300 rounded-md mt-1" data-testid="attachment-kind-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="payment_proof">Payment Proof</SelectItem>
                <SelectItem value="invoice">Invoice</SelectItem>
                <SelectItem value="customer_email">Customer Email</SelectItem>
                <SelectItem value="whatsapp">WhatsApp Screenshot</SelectItem>
                <SelectItem value="fedex_receipt">FedEx Receipt</SelectItem>
                <SelectItem value="customs_doc">Customs Document</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button onClick={upload} disabled={busy || !file} className="bg-zinc-900 hover:bg-zinc-800 text-white rounded-md" data-testid="upload-attachment-button">
            <UploadSimple size={16} weight="bold" className="mr-1" /> Upload
          </Button>
        </div>
      </Card>

      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {attachments.map((att) => {
          // New attachments have download_url; legacy ones have data_url.
          const src = att.download_url
            ? `${process.env.REACT_APP_BACKEND_URL}${att.download_url}`
            : att.data_url;
          const dlHref = att.download_url
            ? `${process.env.REACT_APP_BACKEND_URL}${att.download_url}?inline=0`
            : att.data_url;
          const isImage = att.content_type?.startsWith("image/");
          const isPdf = att.content_type === "application/pdf";
          return (
          <div key={att.attachment_id} className="border border-zinc-200 bg-white p-4" data-testid={`attachment-${att.attachment_id}`}>
            <div className="flex items-center gap-2 mb-3">
              <Paperclip size={14} className="text-zinc-500" />
              <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">{att.kind}</span>
            </div>
            {isImage ? (
              <button
                type="button"
                onClick={() => setPreview({ src, dlHref, filename: att.filename, content_type: att.content_type })}
                className="block w-full group"
                data-testid={`preview-attachment-${att.attachment_id}`}
                title="Click to preview"
              >
                <img src={src} alt={att.filename} className="w-full h-40 object-cover border border-zinc-200 group-hover:opacity-90 transition-opacity" />
                <p className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1 text-left">Click to preview</p>
              </button>
            ) : isPdf ? (
              <button
                type="button"
                onClick={() => setPreview({ src, dlHref, filename: att.filename, content_type: att.content_type })}
                className="block w-full text-left"
                data-testid={`preview-attachment-${att.attachment_id}`}
              >
                <div className="w-full h-40 flex flex-col items-center justify-center bg-zinc-100 border border-zinc-200 group hover:bg-zinc-200 transition-colors">
                  <Paperclip size={22} className="text-zinc-500 mb-2" />
                  <span className="text-xs text-zinc-700 font-semibold">PDF · Click to preview</span>
                </div>
              </button>
            ) : (
              <div className="w-full h-40 flex items-center justify-center bg-zinc-100 border border-zinc-200">
                <a href={dlHref} download={att.filename} className="text-sm text-zinc-700 hover:underline">Download {att.filename}</a>
              </div>
            )}
            <p className="text-sm mt-2 truncate">{att.filename}</p>
            <p className="text-xs text-zinc-500">{formatDateTime(att.created_at)} · {att.uploaded_by}</p>
          </div>
        );})}
        {attachments.length === 0 && (
          <div className="col-span-full text-center py-12 text-sm text-zinc-500">No attachments yet.</div>
        )}
      </div>

      <AttachmentPreviewDialog preview={preview} onClose={() => setPreview(null)} />
    </div>
  );
}

function AttachmentPreviewDialog({ preview, onClose }) {
  return (
    <Dialog open={!!preview} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-5xl max-h-[90vh] p-0 overflow-hidden" data-testid="attachment-preview-dialog">
        <DialogHeader className="px-6 pt-6 pb-3">
          <DialogTitle className="flex items-center justify-between gap-4">
            <span className="truncate text-base">{preview?.filename || "Preview"}</span>
            {preview && (
              <a
                href={preview.dlHref}
                download={preview.filename}
                className="text-xs uppercase tracking-widest text-zinc-500 hover:text-zinc-900 underline whitespace-nowrap"
                data-testid="preview-download-link"
              >
                Download
              </a>
            )}
          </DialogTitle>
        </DialogHeader>
        <div className="bg-zinc-900 flex items-center justify-center min-h-[60vh]">
          {preview && preview.content_type?.startsWith("image/") && (
            <img src={preview.src} alt={preview.filename} className="max-h-[75vh] max-w-full object-contain" data-testid="preview-image" />
          )}
          {preview && preview.content_type === "application/pdf" && (
            <iframe src={preview.src} title={preview.filename} className="w-full h-[75vh] bg-white" data-testid="preview-pdf" />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function NotesTab({ orderId, notes, onDone }) {
  const [content, setContent] = useState("");
  const [busy, setBusy] = useState(false);

  const add = async () => {
    if (!content.trim()) return;
    setBusy(true);
    try {
      await api.post(`/orders/${orderId}/notes`, { content });
      setContent("");
      onDone();
    } catch { toast.error("Failed to add note"); } finally { setBusy(false); }
  };

  return (
    <div>
      <Card title="Add Internal Note">
        <Textarea value={content} onChange={(e) => setContent(e.target.value)} rows={3} placeholder="Customer called about delivery time…" className="border-zinc-300 rounded-md" data-testid="new-note-textarea" />
        <Button onClick={add} disabled={busy || !content.trim()} className="mt-3 bg-zinc-900 hover:bg-zinc-800 text-white rounded-md" data-testid="add-note-button">
          <ChatCircle size={16} weight="bold" className="mr-1" /> Add Note
        </Button>
      </Card>

      <div className="mt-4 space-y-3">
        {notes.map((n) => (
          <div key={n.note_id} className="border border-zinc-200 bg-white p-4" data-testid={`note-${n.note_id}`}>
            <p className="text-sm whitespace-pre-wrap">{n.content}</p>
            <p className="text-xs text-zinc-500 mt-2 font-mono">{n.user_name || n.user_email} · {formatDateTime(n.created_at)}</p>
          </div>
        ))}
        {notes.length === 0 && <p className="text-sm text-zinc-500 py-8 text-center">No notes yet.</p>}
      </div>
    </div>
  );
}

function ShippingTab({ order, canShip, onDone }) {
  const [courier, setCourier] = useState("FedEx");
  const [tracking, setTracking] = useState("");
  const [weight, setWeight] = useState(order.weight || 0);
  const [packages, setPackages] = useState(1);
  const [cost, setCost] = useState(0);
  const [busy, setBusy] = useState(false);
  const [updates, setUpdates] = useState([]);
  const [updateStatus, setUpdateStatus] = useState("In Transit");
  const [updateNotes, setUpdateNotes] = useState("");
  const [notifyCustomer, setNotifyCustomer] = useState(true);

  const loadUpdates = useCallback(async () => {
    if (!order.tracking_number) return;
    try {
      const r = await api.get(`/orders/${order.order_id}/tracking-updates`);
      setUpdates(r.data);
    } catch (e) { devDebug("Tracking updates fetch failed", e); }
  }, [order.order_id, order.tracking_number]);

  useEffect(() => { loadUpdates(); }, [loadUpdates]);

  const submit = async () => {
    if (!tracking) { toast.error("Tracking number required"); return; }
    setBusy(true);
    try {
      await api.post(`/orders/${order.order_id}/ship`, {
        courier, tracking_number: tracking, weight: parseFloat(weight) || 0,
        packages: parseInt(packages, 10) || 1, shipping_cost: parseFloat(cost) || 0,
      });
      toast.success("Shipment created and tracking email sent");
      onDone();
    } catch (error) { toast.error(error.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };

  const addUpdate = async () => {
    if (!updateStatus) return;
    setBusy(true);
    try {
      await api.post(`/orders/${order.order_id}/tracking-update`, {
        status: updateStatus, notes: updateNotes, notify_customer: notifyCustomer,
      });
      toast.success(notifyCustomer ? "Status update saved and customer emailed" : "Status update saved");
      setUpdateNotes("");
      loadUpdates();
      onDone();
    } catch (error) { toast.error(error.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <Card title="Current Shipment">
        <Row label="Courier" value={order.courier} />
        <Row label="Tracking Number" value={order.tracking_number} />
        <Row label="Status" value={order.status} />
      </Card>

      {canShip && !order.tracking_number && (
        <Card title="Create Shipment">
          <div className="space-y-3">
            <div>
              <Label className="text-[11px] uppercase tracking-widest text-zinc-500">Courier</Label>
              <Select value={courier} onValueChange={setCourier}>
                <SelectTrigger className="border-zinc-300 rounded-md mt-1" data-testid="courier-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="FedEx">FedEx</SelectItem>
                  <SelectItem value="DHL">DHL</SelectItem>
                  <SelectItem value="UPS">UPS</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-widest text-zinc-500">Tracking Number</Label>
              <Input value={tracking} onChange={(e) => setTracking(e.target.value)} className="border-zinc-300 rounded-md mt-1 font-mono" data-testid="tracking-input" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-[11px] uppercase tracking-widest text-zinc-500">Weight (kg)</Label>
                <Input type="number" step="0.01" value={weight} onChange={(e) => setWeight(e.target.value)} className="border-zinc-300 rounded-md mt-1" data-testid="ship-weight-input" />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-widest text-zinc-500">Packages</Label>
                <Input type="number" value={packages} onChange={(e) => setPackages(e.target.value)} className="border-zinc-300 rounded-md mt-1" data-testid="packages-input" />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-widest text-zinc-500">Cost</Label>
                <Input type="number" step="0.01" value={cost} onChange={(e) => setCost(e.target.value)} className="border-zinc-300 rounded-md mt-1" data-testid="shipping-cost-input" />
              </div>
            </div>
            <Button onClick={submit} disabled={busy} className="w-full bg-zinc-900 hover:bg-zinc-800 text-white rounded-md" data-testid="create-shipment-button">
              <Truck size={16} weight="bold" className="mr-1" /> Ship & Send Email
            </Button>
          </div>
        </Card>
      )}

      {order.tracking_number && (
        <>
          {canShip && (
            <Card title="Add Tracking Update">
              <div className="space-y-3" data-testid="tracking-update-form">
                <div>
                  <Label className="text-[11px] uppercase tracking-widest text-zinc-500">Status</Label>
                  <Select value={updateStatus} onValueChange={setUpdateStatus}>
                    <SelectTrigger className="border-zinc-300 rounded-md mt-1" data-testid="tracking-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Dispatched">Dispatched</SelectItem>
                      <SelectItem value="In Transit">In Transit</SelectItem>
                      <SelectItem value="Out for Delivery">Out for Delivery</SelectItem>
                      <SelectItem value="Delivered">Delivered</SelectItem>
                      <SelectItem value="Exception">Exception</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-widest text-zinc-500">Notes for customer (optional)</Label>
                  <Textarea value={updateNotes} onChange={(e) => setUpdateNotes(e.target.value)} rows={2} placeholder="Reached Madrid customs · expected delivery Monday" className="border-zinc-300 rounded-md mt-1" data-testid="tracking-notes-input" />
                </div>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={notifyCustomer}
                    onChange={(e) => setNotifyCustomer(e.target.checked)}
                    data-testid="tracking-notify-checkbox"
                  />
                  Email customer about this update
                </label>
                <Button onClick={addUpdate} disabled={busy} className="w-full bg-zinc-900 hover:bg-zinc-800 text-white rounded-md" data-testid="save-tracking-update-button">
                  <MapPin size={16} weight="bold" className="mr-1" /> Save Update
                </Button>
              </div>
            </Card>
          )}
          <Card title={`Tracking History (${updates.length})`}>
            <ol className="space-y-3" data-testid="tracking-history">
              {updates.map((u) => (
                <li key={u.update_id} className="flex gap-3" data-testid={`tracking-update-${u.update_id}`}>
                  <div className="flex flex-col items-center">
                    <div className={`w-2.5 h-2.5 rounded-full ${u.status === "Delivered" ? "bg-emerald-600" : u.status === "Exception" ? "bg-red-600" : "bg-zinc-900"}`} />
                    <div className="w-px flex-1 bg-zinc-200 mt-1" />
                  </div>
                  <div className="pb-3 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-semibold">{u.status}</p>
                      {u.notified_customer && (
                        <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 bg-emerald-50 border border-emerald-200 text-emerald-700 font-semibold flex items-center gap-1">
                          <ChatText size={9} weight="bold" /> Emailed
                        </span>
                      )}
                    </div>
                    {u.notes && <p className="text-xs text-zinc-600 mt-0.5">{u.notes}</p>}
                    <p className="text-[10px] uppercase tracking-widest text-zinc-400 mt-1 font-mono">
                      {formatDateTime(u.created_at)} · {u.created_by}
                    </p>
                  </div>
                </li>
              ))}
              {updates.length === 0 && <li className="text-sm text-zinc-500 text-center py-4">No tracking updates yet.</li>}
            </ol>
          </Card>
        </>
      )}
    </div>
  );
}

function TimelineTab({ activity }) {
  return (
    <div className="border border-zinc-200 bg-white p-6">
      <h3 className="text-lg font-bold tracking-tight mb-4">Activity Timeline</h3>
      <ol className="space-y-4">
        {activity.map((a) => (
          <li key={a.activity_id} className="flex gap-4" data-testid={`activity-${a.activity_id}`}>
            <div className="flex flex-col items-center">
              <div className="w-2 h-2 rounded-full bg-zinc-900" />
              <div className="w-px flex-1 bg-zinc-200 mt-1" />
            </div>
            <div className="pb-4">
              <p className="text-sm font-medium">{a.action}</p>
              {a.details && <p className="text-xs text-zinc-600 mt-0.5">{a.details}</p>}
              <p className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1 font-mono">
                {formatDateTime(a.created_at)} · {a.user_email}
              </p>
            </div>
          </li>
        ))}
        {activity.length === 0 && <p className="text-sm text-zinc-500 text-center py-4">No activity yet.</p>}
      </ol>
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div className="border border-zinc-200 bg-white p-6">
      <h3 className="text-lg font-bold tracking-tight mb-4">{title}</h3>
      {children}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between py-1.5 border-b border-zinc-100 last:border-0">
      <span className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold">{label}</span>
      <span className="text-sm font-medium text-right">{value || "—"}</span>
    </div>
  );
}


function EditOrderDialog({ open, onOpenChange, order, onSaved }) {
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (order) {
      setForm({
        external_order_number: order.external_order_number || "",
        customer_name: order.customer_name || "",
        company: order.company || "",
        email: order.email || "",
        phone: order.phone || "",
        country: order.country || "",
        address: order.address || "",
        product: order.product || "",
        quantity: order.quantity ?? 0,
        weight: order.weight ?? 0,
        total_amount: order.total_amount ?? 0,
        currency: order.currency || "EUR",
        payment_method: order.payment_method || "Bank Transfer",
        payment_reference: order.payment_reference || "",
        remarks: order.remarks || "",
        status: order.status || "New Order",
      });
    }
  }, [order]);

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        ...form,
        quantity: parseInt(form.quantity, 10) || 0,
        weight: parseFloat(form.weight) || 0,
        total_amount: parseFloat(form.total_amount) || 0,
      };
      await api.put(`/orders/${order.order_id}`, payload);
      toast.success("Order updated");
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.value ?? e }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="edit-order-dialog">
        <DialogHeader>
          <DialogTitle>Edit Order #{order?.order_number}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 mt-2">
          <EField label="Form Order # (external)"><Input value={form.external_order_number} onChange={set("external_order_number")} placeholder="From Google Form, if any" data-testid="edit-external-order-number" /></EField>
          <EField label="Customer Name"><Input value={form.customer_name} onChange={set("customer_name")} data-testid="edit-customer-name" /></EField>
          <EField label="Company"><Input value={form.company} onChange={set("company")} data-testid="edit-company" /></EField>
          <EField label="Email"><Input value={form.email} onChange={set("email")} data-testid="edit-email" /></EField>
          <EField label="Phone"><Input value={form.phone} onChange={set("phone")} data-testid="edit-phone" /></EField>
          <EField label="Country"><Input value={form.country} onChange={set("country")} data-testid="edit-country" /></EField>
          <EField label="Currency">
            <Select value={form.currency} onValueChange={(v) => setForm((f) => ({ ...f, currency: v }))}>
              <SelectTrigger data-testid="edit-currency"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["EUR", "USD", "GBP", "INR"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </EField>
        </div>
        <EField label="Address" full>
          <Textarea rows={2} value={form.address} onChange={set("address")} data-testid="edit-address" />
        </EField>
        <div className="grid grid-cols-4 gap-3">
          <EField label="Product"><Input value={form.product} onChange={set("product")} data-testid="edit-product" /></EField>
          <EField label="Quantity"><Input type="number" value={form.quantity} onChange={set("quantity")} data-testid="edit-quantity" /></EField>
          <EField label="Weight (kg)"><Input type="number" step="0.01" value={form.weight} onChange={set("weight")} data-testid="edit-weight" /></EField>
          <EField label="Total Amount"><Input type="number" step="0.01" value={form.total_amount} onChange={set("total_amount")} data-testid="edit-total" /></EField>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <EField label="Payment Method">
            <Select value={form.payment_method} onValueChange={(v) => setForm((f) => ({ ...f, payment_method: v }))}>
              <SelectTrigger data-testid="edit-payment-method"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["Bank Transfer", "PayPal", "Stripe", "Cash", "UPI", "Other"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          </EField>
          <EField label="Payment Reference"><Input value={form.payment_reference} onChange={set("payment_reference")} data-testid="edit-payment-ref" /></EField>
          <EField label="Status">
            <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
              <SelectTrigger data-testid="edit-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ORDER_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </EField>
        </div>
        <EField label="Remarks" full>
          <Textarea rows={2} value={form.remarks} onChange={set("remarks")} data-testid="edit-remarks" />
        </EField>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="edit-cancel">Cancel</Button>
          <Button onClick={save} disabled={busy} className="bg-zinc-900 hover:bg-zinc-800 text-white" data-testid="edit-save">
            {busy ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function EField({ label, children, full }) {
  return (
    <div className={full ? "col-span-full" : ""}>
      <Label className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
